import { match } from 'ts-pattern';

import {
  useMarketSelectionStore,
  type MarketSelection,
  type MarketType,
  type TeamType,
  type Side,
} from '../state/marketSelection.store';
import { type NormalizedMarketGroup } from '../state/normalizedMarkets.normalization';
import { useNormalizedMarketsStore } from '../state/normalizedMarkets.store';

export function createSelection(
  marketGroupId: string,
  marketId: string,
  marketType: MarketType,
  teamType: TeamType,
  side: Side
): MarketSelection {
  return {
    marketGroupId,
    marketId,
    marketType,
    teamType,
    side,
  };
}

export function createSelectionFromMarketGroup(
  marketGroup: NormalizedMarketGroup,
  marketType: MarketType,
  teamType: TeamType,
  homeTeamId: string,
  awayTeamId: string
): MarketSelection | null {
  const { homeTeamLine, awayTeamLine } = marketGroup;

  const result = match(marketType)
    .with('moneyline', () => {
      const moneyline = teamType === 'home' ? homeTeamLine.moneyline : awayTeamLine.moneyline;
      return moneyline ? { marketId: moneyline.marketId, side: moneyline.side } : null;
    })
    .with('spread', () => {
      const spread = teamType === 'home' ? homeTeamLine.spread : awayTeamLine.spread;
      if (!spread) return null;

      const spreadMarket = marketGroup.markets.find((m) => m.type === 'spread');
      const teamId = teamType === 'home' ? homeTeamId : awayTeamId;
      const isYesSide =
        !!spreadMarket && spreadMarket.type === 'spread' && spreadMarket.yes_team_id === teamId;

      return { marketId: spread.marketId, side: isYesSide ? 'yes' : ('no' as Side) };
    })
    .with('total', () => {
      const total = teamType === 'home' ? homeTeamLine.total : awayTeamLine.total;
      if (!total) return null;

      const isOver = teamType === 'away';

      return { marketId: total.marketId, side: isOver ? 'yes' : ('no' as Side) };
    })
    .with('future_team_champion', () => null)
    .with('player_prop', () => null)
    .exhaustive();

  if (!result || !result.marketId) return null;

  const { marketId, side } = result;

  return createSelection(marketGroup.id, marketId, marketType, teamType, side);
}

export function resolveGameSelection(
  marketGroupId: string,
  marketType: MarketType,
  teamType: TeamType
): MarketSelection | null {
  const marketGroup = useNormalizedMarketsStore
    .getState()
    .normalizedMarketGroupsById.get(marketGroupId);
  if (!marketGroup) return null;

  return createSelectionFromMarketGroup(
    marketGroup,
    marketType,
    teamType,
    marketGroup.homeTeamId,
    marketGroup.awayTeamId
  );
}

export function useIsMarketSelected(
  marketGroupId: string,
  marketType: MarketType,
  teamType: TeamType,
  side?: Side
): boolean {
  return useMarketSelectionStore(
    (state) => {
      const current = state.selections[0];
      if (!current) return false;

      return (
        current.marketGroupId === marketGroupId &&
        current.marketType === marketType &&
        current.teamType === teamType &&
        (side === undefined || current.side === side)
      );
    },
    (prev, next) => prev === next
  );
}

export function useIsFutureMarketSelected(marketId: string, side: Side): boolean {
  return useMarketSelectionStore(
    (state) => {
      const current = state.selections[0];
      if (!current) return false;

      return current.marketId === marketId && current.side === side;
    },
    (prev, next) => prev === next
  );
}

export function useIsPlayerPropSelected(marketId: string, side: Side): boolean {
  return useMarketSelectionStore(
    (state) => {
      const current = state.selections[0];
      if (!current || current.marketType !== 'player_prop') return false;

      return current.marketId === marketId && current.side === side;
    },
    (prev, next) => prev === next
  );
}
